/* Pet Nugget dog age calculator. Pure helpers are exported for Node tests; in the browser every
 * [data-pn-dog-age] root is mounted (also through window.PNDogAge.mount). Breed data comes from
 * pn-dog-age-data.js. Email signup uses the public Klaviyo client API only and never delays the result. */
(function (host, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!host || !host.document) return;
  host.PNDogAge = api;
  function boot(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll('[data-pn-dog-age]').forEach(function (element) { api.mount(element, host); });
  }
  if (host.document.readyState === 'loading') host.document.addEventListener('DOMContentLoaded', function () { boot(host.document); });
  else boot(host.document);
  host.document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  var SIZES = ['small', 'medium', 'large', 'giant'];
  var STAGES = ['puppy', 'young', 'mature', 'senior'];
  var RATE = { small: 4, medium: 5, large: 6, giant: 7 };
  var PUPPY_END = { small: 0.5, medium: 7 / 12, large: 0.75, giant: 0.75 };
  var YOUNG_END = { small: 3, medium: 3, large: 4, giant: 4 };
  var MAX_AGE = 30;
  var MIXED = 'mixed';
  var SIZE_LABEL = { small: 'Small', medium: 'Medium', large: 'Large', giant: 'Giant' };
  var STAGE_LABEL = { puppy: 'Puppy', young: 'Young adult', mature: 'Mature adult', senior: 'Senior' };
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var ID_RE = /^[a-zA-Z0-9]{6}$/;
  var KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions?company_id=';
  var POPULAR = ['Labrador Retriever', 'French Bulldog', 'Golden Retriever', 'German Shepherd', 'Poodle (any size)', 'Bulldog', 'Rottweiler', 'Beagle', 'Dachshund (Standard)', 'German Shorthaired Pointer', 'Pembroke Welsh Corgi', 'Australian Shepherd', 'Yorkshire Terrier', 'Cavalier King Charles Spaniel', 'Doberman Pinscher', 'Boxer', 'Miniature Schnauzer', 'Cane Corso', 'Great Dane', 'Shih Tzu', 'Chihuahua', 'Siberian Husky', 'Bernese Mountain Dog', 'Pomeranian', 'Boston Terrier', 'Havanese', 'Border Collie'];
  var STAGE_COPY = {
    puppy: {
      title: 'Still growing',
      intro: 'Everything is new, and the body is changing week to week.',
      habits: ['Puppy food matched to adult size. Big-breed puppies do best on a large-breed puppy formula.', 'Expect vet visits every few weeks for vaccines and growth checks.', 'Short, gentle play beats long runs while joints are still forming.', 'New people, sounds and places now pay off for years.']
    },
    young: {
      title: 'Prime time',
      intro: 'Fully grown, full of energy, and usually the easiest years for health.',
      habits: ['A yearly vet check and a steady weight are the big two.', 'Daily exercise plus training games gives all that energy a job.', 'Start brushing teeth now. Keeping them clean is easier than fixing them later.', 'Lock in the routine: same food, same walk times, same bedtime.']
    },
    mature: {
      title: 'The middle years',
      intro: 'Still playful, but this is when small changes start to show up.',
      habits: ['Notice the little things, like a slower start on the stairs or a skipped last lap.', 'Keep the yearly vet check and ask about baseline bloodwork while everything looks normal.', 'Watch weight and muscle. They can move in opposite directions.', 'Keep walks regular and mix in low-impact fun like swimming or sniff walks.']
    },
    senior: {
      title: 'The golden years',
      intro: 'Slower mornings, same big heart. Comfort and routine matter most now.',
      habits: ['Many vets like to see senior dogs twice a year.', 'Shorter, more frequent walks. Add a soft bed and rugs on slippery floors.', 'Changes in appetite, thirst, sleep or bathroom habits are worth a call to your vet.', 'Keep the mind busy with new smells, easy puzzles and time with you.']
    }
  };
  var MESSAGES = {
    breed: "Pick a breed, or choose mixed breed if you're not sure.",
    size: 'Pick the size closest to your dog.',
    ageMissing: "Add your dog's age. A rough guess is fine.",
    ageWhole: 'Use whole years here, then pick the months.',
    ageMax: 'Enter an age of 30 years or less.',
    email: "That email doesn't look right. Fix it or clear the field."
  };
  var SMALL_SAMPLE = " That breed's number comes from a small sample, so hold it loosely.";
  var FINE_PRINT = 'An estimate for curiosity and planning, not a diagnosis. Your vet knows your dog best.';
  var sent = {}; // Addresses already sent to Klaviyo in this page session. Memory only.

  /* ---------------- Pure helpers ---------------- */
  function humanYears(ageYears, sizeClass) {
    var age = Number(ageYears);
    var rate = RATE[sizeClass];
    if (!isFinite(age) || age < 0 || !rate) return NaN;
    if (age <= 1) return 15 * age;
    if (age <= 2) return 15 + 9 * (age - 1);
    return 24 + rate * (age - 2);
  }
  function stageBounds(lifespanYears, sizeClass) {
    var youngEnd = YOUNG_END[sizeClass];
    return { puppyEnd: PUPPY_END[sizeClass], youngEnd: youngEnd, seniorStart: Math.max(0.75 * Number(lifespanYears), youngEnd + 1) };
  }
  function lifeStage(ageYears, lifespanYears, sizeClass) {
    var bounds = stageBounds(lifespanYears, sizeClass);
    var age = Number(ageYears);
    if (age < bounds.puppyEnd) return 'puppy';
    if (age < bounds.youngEnd) return 'young';
    if (age < bounds.seniorStart) return 'mature';
    return 'senior';
  }
  function lifespanFor(breed, sizeClass, data) {
    if (breed && isFinite(Number(breed.l)) && Number(breed.l) > 0) return Number(breed.l);
    var bySize = data && data.mixedBySize ? Number(data.mixedBySize[sizeClass]) : NaN;
    return isFinite(bySize) && bySize > 0 ? bySize : Number(data && data.mixedMedian) || 12;
  }
  function validEmail(value) {
    value = String(value == null ? '' : value);
    return value.length <= 254 && EMAIL_RE.test(value);
  }
  function validId(value) { return ID_RE.test(String(value == null ? '' : value)); }
  function findBreed(data, name) {
    var list = data && Array.isArray(data.breeds) ? data.breeds : [];
    for (var i = 0; i < list.length; i++) if (list[i].n === name) return list[i];
    return null;
  }
  // The size question is needed for mixed breeds and for entries that cover several sizes.
  function needsSize(breedValue, data) {
    if (breedValue === MIXED) return true;
    var breed = findBreed(data, breedValue);
    return !!breed && SIZES.indexOf(breed.s) === -1;
  }
  function plural(count, word) { return count + ' ' + word + (count === 1 ? '' : 's'); }
  function ageText(years, months) {
    var parts = [];
    if (years > 0) parts.push(plural(years, 'year'));
    if (months > 0 || !parts.length) parts.push(plural(months, 'month'));
    return parts.join(', ');
  }
  function ageShort(years, months) {
    var parts = [];
    if (years > 0) parts.push(years + ' yr');
    if (months > 0 || !parts.length) parts.push(months + ' mo');
    return parts.join(' ');
  }
  function trimNumber(value) { return String(Math.round(Number(value) * 10) / 10); }
  // "9 mo" below one year when it is a whole number of months, otherwise "9.8 yr".
  function boundText(years) {
    var months = Math.round(years * 12);
    if (years < 1 && Math.abs(years * 12 - months) < 1e-6) return months + ' mo';
    return trimNumber(years) + ' yr';
  }
  function article(word) { return /^[aeiou]/i.test(String(word)) ? 'an' : 'a'; }
  // "Poodle (any size)" reads oddly inside a sentence once the size is known.
  function sentenceName(displayName) { return String(displayName).replace(/\s*\(any size\)$/i, ''); }
  function birthEstimate(ageYears, now) {
    var today = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
    var date = new Date(today.getFullYear(), today.getMonth() - Math.round(Number(ageYears) * 12), 1);
    var month = date.getMonth() + 1;
    return date.getFullYear() + '-' + (month < 10 ? '0' : '') + month;
  }
  // Links: site paths, same-page anchors and http(s) only.
  function safeUrl(value) {
    value = String(value == null ? '' : value).trim();
    return /^(https?:\/\/|\/(?!\/)|#)/i.test(value) ? value : '';
  }
  // Images: anything except a non-http scheme (Shopify file URLs start with //).
  function imageUrl(value) {
    value = String(value == null ? '' : value).trim();
    return /^[a-z][a-z0-9+.-]*:/i.test(value) && !/^https?:\/\//i.test(value) ? '' : value;
  }

  // raw: { name, breed, size, years, months, email, badYears }. Returns { ok, errors, value }.
  function evaluate(raw, data, options) {
    raw = raw || {};
    var errors = {};
    var name = String(raw.name || '').replace(/\s+/g, ' ').trim().slice(0, 40);
    var breedValue = String(raw.breed || '');
    var breed = breedValue === MIXED ? null : findBreed(data, breedValue);
    var size = null;
    if (breedValue !== MIXED && !breed) errors.breed = MESSAGES.breed;
    else if (needsSize(breedValue, data)) {
      if (SIZES.indexOf(raw.size) === -1) errors.size = MESSAGES.size;
      else size = raw.size;
    } else size = breed.s;

    var yearsText = String(raw.years == null ? '' : raw.years).trim();
    var monthsText = String(raw.months == null ? '' : raw.months).trim();
    var years = yearsText === '' ? 0 : Number(yearsText);
    var months = monthsText === '' ? 0 : Number(monthsText);
    var age = NaN;
    if (raw.badYears || !/^\d*$/.test(yearsText) || !/^\d*$/.test(monthsText) || months > 11) errors.age = MESSAGES.ageWhole;
    else {
      age = years + months / 12;
      if (age <= 0) errors.age = MESSAGES.ageMissing;
      else if (age > MAX_AGE) errors.age = MESSAGES.ageMax;
    }

    var email = options && options.email ? String(raw.email || '').trim() : '';
    if (email && !validEmail(email)) errors.email = MESSAGES.email;

    var ok = !errors.breed && !errors.size && !errors.age && !errors.email;
    return {
      ok: ok,
      errors: errors,
      value: ok ? { name: name, breed: breed, breedName: breed ? breed.n : 'Mixed breed', size: size, years: years, months: months, ageYears: age, email: email } : null
    };
  }

  // Everything the result view shows, as plain text and numbers.
  function resultModel(value, data) {
    var size = value.size;
    var age = value.ageYears;
    var human = humanYears(age, size);
    var rounded = Math.round(human);
    var lifespan = lifespanFor(value.breed, size, data);
    var bounds = stageBounds(lifespan, size);
    var stage = lifeStage(age, lifespan, size);
    var study = data && data.study && data.study.label ? data.study.label : 'a 2024 UK study of 584,734 dogs';
    var lifespanLine;
    if (value.breed) {
      var breedName = sentenceName(value.breed.n);
      lifespanLine = 'Typical lifespan for ' + article(breedName) + ' ' + breedName + ': about ' + trimNumber(lifespan) + ' years (median in ' + study + ').';
      if (value.breed.low) lifespanLine += SMALL_SAMPLE;
    } else {
      lifespanLine = 'Typical lifespan for a ' + size + ' mixed-breed dog: about ' + trimNumber(lifespan) + ' years (based on ' + study + ').';
    }
    var max = Math.max(lifespan * 1.15, age * 1.05);
    var edges = [0, bounds.puppyEnd, bounds.youngEnd, bounds.seniorStart, max];
    var ranges = ['0 to ' + boundText(bounds.puppyEnd), boundText(bounds.puppyEnd) + ' to ' + boundText(bounds.youngEnd), boundText(bounds.youngEnd) + ' to ' + boundText(bounds.seniorStart), boundText(bounds.seniorStart) + ' and up'];
    var segments = STAGES.map(function (key, index) {
      var start = Math.min(edges[index], max);
      var end = Math.min(Math.max(edges[index + 1], start), max);
      return { stage: key, label: STAGE_LABEL[key], range: ranges[index], percent: (end - start) / max * 100, current: key === stage };
    });
    return {
      name: value.name,
      breedName: value.breedName,
      size: size,
      stage: stage,
      stageLabel: STAGE_LABEL[stage],
      humanYears: human,
      humanAge: rounded,
      eyebrow: (value.name ? value.name + "'s" : "Your dog's") + ' age in human years',
      numberText: rounded < 1 ? 'Under 1' : 'About ' + rounded,
      unitText: rounded <= 1 ? 'human year' : 'human years',
      subLine: ageText(value.years, value.months) + ' old \u00b7 ' + (value.breed ? sentenceName(value.breed.n) : 'Mixed breed') + ' \u00b7 ' + SIZE_LABEL[size] + ' dog',
      lifespan: lifespan,
      lifespanLine: lifespanLine,
      bounds: bounds,
      bar: { max: max, marker: Math.max(0, Math.min(100, age / max * 100)), markerLabel: ageShort(value.years, value.months), segments: segments },
      copy: STAGE_COPY[stage]
    };
  }

  // fields: { email, pet_name, pet_breed, pet_size, pet_age_years, pet_life_stage, calculator_placement, now }
  function subscriptionPayload(fields, listId) {
    fields = fields || {};
    var email = String(fields.email || '').trim();
    var age = Number(fields.pet_age_years);
    if (!validEmail(email) || !validId(listId)) throw new Error('Invalid signup');
    if (!(age > 0 && age <= MAX_AGE) || SIZES.indexOf(fields.pet_size) === -1 || STAGES.indexOf(fields.pet_life_stage) === -1) throw new Error('Invalid signup');
    var properties = {};
    var name = String(fields.pet_name || '').trim().slice(0, 40);
    // An empty name is left out so it cannot blank a name the profile already has.
    if (name) properties.pet_name = name;
    properties.pet_breed = String(fields.pet_breed || 'Mixed breed').slice(0, 80);
    properties.pet_size = fields.pet_size;
    properties.pet_age_years = Math.round(age * 10) / 10;
    properties.pet_birth_estimate = birthEstimate(age, fields.now);
    properties.pet_human_age = Math.round(humanYears(age, fields.pet_size));
    properties.pet_life_stage = fields.pet_life_stage;
    properties.petnugget_signup_source = 'dog_age_calculator';
    properties.calculator_placement = fields.calculator_placement === 'article' ? 'article' : 'page';
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Pet Nugget dog age calculator',
          profile: { data: { type: 'profile', attributes: {
            email: email,
            properties: properties,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: String(listId) } } }
      }
    };
  }

  /* ---------------- Browser ---------------- */
  function mount(root, win) {
    win = win || (typeof window !== 'undefined' ? window : null);
    if (!root || !win || !root.dataset || root.dataset.pnDogAgeMounted) return;
    var doc = win.document;
    var data = win.PNDogAgeData;
    var form = root.querySelector('[data-pn-dog-age-form]');
    var result = root.querySelector('[data-pn-dog-age-result]');
    // Without the data file there is nothing to mount yet. A later call can still succeed.
    if (!data || !Array.isArray(data.breeds) || !form || !result) return;
    root.dataset.pnDogAgeMounted = 'true';

    var config = {};
    try { config = JSON.parse(root.querySelector('[data-pn-dog-age-config]').textContent) || {}; } catch (_) { config = {}; }
    var placement = config.placement === 'article' ? 'article' : 'page';
    var images = config.images && typeof config.images === 'object' ? config.images : {};
    var links = config.links && typeof config.links === 'object' ? config.links : {};
    var emailEnabled = validId(config.companyId) && validId(config.listId);

    var head = root.querySelector('[data-pn-dog-age-head]');
    var title = root.querySelector('[data-pn-dog-age-title]');
    var breedSelect = form.elements.breed;
    var sizeField = root.querySelector('[data-pn-dog-age-size]');
    var emailField = root.querySelector('[data-pn-dog-age-email]');
    var current = null;

    // nugget.js moves this block out of a store that is then deleted. A copy of the stylesheet link
    // in <head> keeps the styles applied while the original link travels with the block.
    var sheet = root.querySelector('link[rel="stylesheet"]');
    if (sheet && sheet.href && !doc.head.querySelector('link[data-pn-dog-age-css]')) {
      var copy = doc.createElement('link');
      copy.rel = 'stylesheet';
      copy.href = sheet.href;
      copy.setAttribute('data-pn-dog-age-css', '');
      doc.head.appendChild(copy);
    }

    function el(tag, className, text) {
      var node = doc.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }
    function option(value, label) { var node = el('option', '', label); node.value = value; return node; }

    /* Breed list */
    if (breedSelect && !breedSelect.querySelector('optgroup')) {
      if (!breedSelect.querySelector('option[value=""]')) {
        var placeholder = option('', 'Choose a breed');
        placeholder.disabled = true; placeholder.selected = true;
        breedSelect.insertBefore(placeholder, breedSelect.firstChild);
      }
      if (!breedSelect.querySelector('option[value="' + MIXED + '"]')) breedSelect.appendChild(option(MIXED, 'Mixed breed or not sure'));
      var popular = el('optgroup');
      popular.label = 'Popular breeds';
      POPULAR.forEach(function (name) { if (findBreed(data, name)) popular.appendChild(option(name, name)); });
      var all = el('optgroup');
      all.label = 'All breeds A to Z';
      data.breeds.forEach(function (breed) { all.appendChild(option(breed.n, breed.n)); });
      if (popular.children.length) breedSelect.appendChild(popular);
      breedSelect.appendChild(all);
    }

    /* Email: never collect an address that has nowhere to go. */
    if (!emailEnabled && emailField) { emailField.parentNode.removeChild(emailField); emailField = null; }
    if (!emailField) emailEnabled = false;

    function errorNode(key) { return root.querySelector('[data-pn-dog-age-error="' + key + '"]'); }
    function controlsFor(key) {
      if (key === 'breed') return breedSelect ? [breedSelect] : [];
      if (key === 'size') return Array.prototype.slice.call(form.querySelectorAll('input[name="size"]'));
      if (key === 'age') return [form.elements.age_years, form.elements.age_months].filter(Boolean);
      if (key === 'email') return form.elements.email ? [form.elements.email] : [];
      return [];
    }
    function setError(key, message) {
      var node = errorNode(key);
      if (node) { node.textContent = message || ''; node.hidden = !message; }
      controlsFor(key).forEach(function (control) {
        if (message) control.setAttribute('aria-invalid', 'true'); else control.removeAttribute('aria-invalid');
      });
    }
    function syncSize() {
      if (!sizeField || !breedSelect) return;
      var show = needsSize(breedSelect.value, data);
      sizeField.hidden = !show;
      if (!show) setError('size', '');
    }
    function readForm() {
      var yearsInput = form.elements.age_years;
      var checked = form.querySelector('input[name="size"]:checked');
      return {
        name: form.elements.pet_name ? form.elements.pet_name.value : '',
        breed: breedSelect ? breedSelect.value : '',
        size: checked ? checked.value : '',
        years: yearsInput ? yearsInput.value : '',
        months: form.elements.age_months ? form.elements.age_months.value : '0',
        email: emailEnabled && form.elements.email ? form.elements.email.value : '',
        badYears: !!(yearsInput && yearsInput.validity && yearsInput.validity.badInput)
      };
    }
    function track(action, model) {
      // Event data never includes the email address or the dog's name.
      var detail = { calculator: 'dog-age', action: action, stage: model ? model.stage : null, size: model ? model.size : null, breed: model ? model.breedName : null, placement: placement };
      try { root.dispatchEvent(new win.CustomEvent('petnugget:calculator', { bubbles: true, detail: detail })); } catch (_) { /* Analytics must never break the calculator. */ }
    }
    function reveal(target) {
      if (!target) return;
      try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
      // Keep the top of the card clear of the sticky header (scroll-margin-top is set in the CSS).
      var clear = parseFloat(win.getComputedStyle(root).scrollMarginTop) || 0;
      var top = root.getBoundingClientRect().top;
      if (top < clear || top > win.innerHeight * 0.6) root.scrollIntoView({ block: 'start' });
    }

    /* Result view */
    function stageCard(model) {
      var card = el('div', 'pn-calc__stage-card pn-calc__stage-card--' + model.stage);
      var src = imageUrl(images[model.stage]);
      if (src) {
        var frame = el('div', 'pn-calc__stage-img');
        var img = doc.createElement('img');
        img.alt = '';
        img.width = 1000; img.height = 750;
        img.decoding = 'async';
        // A missing file hides the frame and lets the copy use the full width.
        img.onerror = function () { if (frame.parentNode) frame.parentNode.removeChild(frame); card.classList.add('pn-calc__stage-card--plain'); };
        img.src = src;
        frame.appendChild(img);
        card.appendChild(frame);
      } else card.classList.add('pn-calc__stage-card--plain');
      var body = el('div', 'pn-calc__stage-body');
      body.appendChild(el('p', 'eyebrow pn-calc__stage-eyebrow', model.stageLabel));
      body.appendChild(el('p', 'pn-calc__stage-title', model.copy.title));
      body.appendChild(el('p', 'pn-calc__stage-intro', model.copy.intro));
      var list = el('ul', 'pn-calc__habits');
      model.copy.habits.forEach(function (habit) { list.appendChild(el('li', '', habit)); });
      body.appendChild(list);
      var wanted = [links[model.stage], links.chart].filter(function (link) {
        if (!link || !safeUrl(link.url) || !String(link.label || '').trim()) return false;
        // A same-page link is only useful when its target is on this page.
        if (link.url.charAt(0) === '#') return link.url.length > 1 && !!doc.getElementById(link.url.slice(1));
        return true;
      });
      if (wanted.length) {
        var linkList = el('ul', 'pn-calc__links');
        wanted.forEach(function (link) {
          var item = el('li');
          var anchor = el('a', 'pn-calc__link', String(link.label).trim());
          anchor.href = safeUrl(link.url);
          item.appendChild(anchor);
          linkList.appendChild(item);
        });
        body.appendChild(linkList);
      }
      card.appendChild(body);
      return card;
    }
    function render(model) {
      while (result.firstChild) result.removeChild(result.firstChild);

      var top = el('div', 'pn-calc__result-head');
      top.setAttribute('tabindex', '-1');
      top.setAttribute('data-pn-dog-age-result-heading', '');
      top.appendChild(el('p', 'eyebrow eyebrow--green pn-calc__result-eyebrow', model.eyebrow));
      var number = el('p', 'pn-calc__number');
      number.appendChild(el('span', 'pn-calc__number-value', model.numberText));
      number.appendChild(doc.createTextNode(' '));
      number.appendChild(el('span', 'pn-calc__number-unit', model.unitText));
      top.appendChild(number);
      top.appendChild(el('p', 'pn-calc__sub', model.subLine));
      result.appendChild(top);

      var stages = el('div', 'pn-calc__stages');
      var now = el('p', 'pn-calc__stage-now', 'Life stage: ');
      now.appendChild(el('strong', '', model.stageLabel));
      stages.appendChild(now);
      var bar = el('div', 'pn-calc__bar');
      bar.setAttribute('aria-hidden', 'true');
      var rail = el('div', 'pn-calc__track');
      model.bar.segments.forEach(function (segment) {
        var part = el('span', 'pn-calc__seg pn-calc__seg--' + segment.stage + (segment.current ? ' is-current' : ''));
        part.style.width = segment.percent.toFixed(3) + '%';
        rail.appendChild(part);
      });
      bar.appendChild(rail);
      // The CSS places the dot at --pn-calc-at and slides the label so it never leaves the bar.
      bar.style.setProperty('--pn-calc-at', model.bar.marker.toFixed(3) + '%');
      bar.appendChild(el('span', 'pn-calc__marker-label', model.bar.markerLabel));
      bar.appendChild(el('span', 'pn-calc__marker-dot'));
      stages.appendChild(bar);
      var legend = el('ul', 'pn-calc__legend');
      model.bar.segments.forEach(function (segment) {
        var item = el('li', 'pn-calc__legend-item' + (segment.current ? ' is-current' : ''));
        item.appendChild(el('span', 'pn-calc__swatch pn-calc__seg--' + segment.stage));
        var text = el('span', 'pn-calc__legend-text');
        text.appendChild(el('span', 'pn-calc__legend-name', segment.label));
        if (segment.current) text.appendChild(el('span', 'sr-only', ' (current stage)'));
        text.appendChild(el('span', 'pn-calc__legend-range', segment.range));
        item.appendChild(text);
        legend.appendChild(item);
      });
      stages.appendChild(legend);
      stages.appendChild(el('p', 'pn-calc__lifespan', model.lifespanLine));
      result.appendChild(stages);

      result.appendChild(stageCard(model));

      var foot = el('div', 'pn-calc__result-foot');
      var edit = el('button', 'btn btn-secondary pn-calc__edit', 'Change the details');
      edit.type = 'button';
      edit.setAttribute('data-pn-dog-age-edit', '');
      foot.appendChild(edit);
      foot.appendChild(el('p', 'fine pn-calc__fine', FINE_PRINT));
      result.appendChild(foot);
    }
    function showResult() {
      form.hidden = true;
      if (head) head.hidden = true;
      result.hidden = false;
      root.setAttribute('data-pn-dog-age-state', 'result');
      reveal(result.querySelector('[data-pn-dog-age-result-heading]'));
    }
    function showForm() {
      result.hidden = true;
      form.hidden = false;
      if (head) head.hidden = false;
      root.setAttribute('data-pn-dog-age-state', 'form');
      // Focus the title, not a field, so a phone keyboard stays closed until a field is tapped.
      reveal(title || form);
    }

    /* Klaviyo: fire and forget. The visitor already has the result. */
    function subscribe(value, model, honeypot) {
      if (!emailEnabled || !value.email || honeypot || typeof win.fetch !== 'function') return;
      var key = value.email.toLowerCase();
      if (sent[key]) return;
      var body;
      try {
        body = JSON.stringify(subscriptionPayload({
          email: value.email, pet_name: value.name, pet_breed: model.breedName, pet_size: model.size,
          pet_age_years: value.ageYears, pet_life_stage: model.stage, calculator_placement: placement
        }, config.listId));
      } catch (_) { return; }
      sent[key] = true;
      var controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      var timeout = controller ? win.setTimeout(function () { controller.abort(); }, 15000) : 0;
      function failed() { delete sent[key]; }
      try {
        win.fetch(KLAVIYO_URL + encodeURIComponent(config.companyId), {
          method: 'POST',
          headers: { Accept: 'application/vnd.api+json', 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: body,
          signal: controller ? controller.signal : undefined
        }).then(function (response) {
          win.clearTimeout(timeout);
          // 202 may have an empty body. Anything else is dropped without telling the visitor.
          if (response.status !== 202) { failed(); return; }
          track('email_submitted', model);
        }).catch(function () { win.clearTimeout(timeout); failed(); });
      } catch (_) { win.clearTimeout(timeout); failed(); }
    }

    /* Events */
    if (breedSelect) breedSelect.addEventListener('change', function () { setError('breed', ''); syncSize(); });
    form.addEventListener('input', function (event) {
      var name = event.target && event.target.name;
      if (name === 'age_years' || name === 'age_months') setError('age', '');
      else if (name === 'email') setError('email', '');
    });
    form.addEventListener('change', function (event) {
      var name = event.target && event.target.name;
      if (name === 'size') setError('size', '');
      else if (name === 'age_months') setError('age', '');
    });
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var raw = readForm();
      var outcome = evaluate(raw, data, { email: emailEnabled });
      ['breed', 'size', 'age', 'email'].forEach(function (key) { setError(key, outcome.errors[key] || ''); });
      if (!outcome.ok) {
        var firstKey = ['breed', 'size', 'age', 'email'].filter(function (key) { return outcome.errors[key]; })[0];
        var first = controlsFor(firstKey)[0];
        if (first && typeof first.focus === 'function') first.focus();
        return;
      }
      current = resultModel(outcome.value, data);
      render(current);
      showResult();
      track('calculated', current);
      subscribe(outcome.value, current, form.elements.website ? form.elements.website.value : '');
    });
    result.addEventListener('click', function (event) {
      var button = event.target && event.target.closest ? event.target.closest('[data-pn-dog-age-edit]') : null;
      if (!button || !result.contains(button)) return;
      showForm();
      track('recalculate', current);
    });

    syncSize();
    root.setAttribute('data-pn-dog-age-state', 'form');
  }

  return {
    mount: mount, humanYears: humanYears, stageBounds: stageBounds, lifeStage: lifeStage, lifespanFor: lifespanFor,
    subscriptionPayload: subscriptionPayload, validEmail: validEmail, validId: validId, findBreed: findBreed, needsSize: needsSize,
    ageText: ageText, ageShort: ageShort, birthEstimate: birthEstimate, evaluate: evaluate, resultModel: resultModel, safeUrl: safeUrl, imageUrl: imageUrl,
    POPULAR: POPULAR, STAGE_COPY: STAGE_COPY, MESSAGES: MESSAGES
  };
});
